/**
 * The few line icons the body scan screens need that the portal set (./icons.tsx) does not have: a fitted top, a
 * phone, stepping back, light and turning. Same 24 unit grid, stroke and currentColor as the portal set.
 */

import type { ReactNode } from 'react';

type IconProps = { size?: number; className?: string; strokeWidth?: number };

function line(children: ReactNode, { size = 22, className = '', strokeWidth = 1.8 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** A fitted top. */
export const ShirtLine = (p: IconProps) =>
  line(<path d="M9 3.8 4.6 6.2l1.6 4 1.8-.8V20h8V9.4l1.8.8 1.6-4L15 3.8c-.4 1.4-1.6 2.3-3 2.3s-2.6-.9-3-2.3Z" />, p);

/** A phone standing up. */
export const PhoneLine = (p: IconProps) =>
  line(
    <>
      <rect x="7" y="2.8" width="10" height="18.4" rx="2.2" />
      <path d="M11 18h2" />
    </>,
    p,
  );

/** Stepping back from the phone. */
export const StepBackLine = (p: IconProps) =>
  line(
    <>
      <circle cx="15.2" cy="4.6" r="1.9" />
      <path d="M15.2 6.8v6.4M12.4 9.4h5.6M13.4 20.4l1.8-7.2 1.8 7.2" />
      <path d="M9 12H3.4M5.6 9.6 3.2 12l2.4 2.4" />
    </>,
    p,
  );

/** Light. */
export const SunLine = (p: IconProps) =>
  line(
    <>
      <circle cx="12" cy="12" r="3.8" />
      <path d="M12 3v1.8M12 19.2V21M3 12h1.8M19.2 12H21M5.6 5.6l1.3 1.3M17.1 17.1l1.3 1.3M5.6 18.4l1.3-1.3M17.1 6.9l1.3-1.3" />
    </>,
    p,
  );

/** Turning slowly in a circle. */
export const TurnLine = (p: IconProps) =>
  line(
    <>
      <path d="M19.6 12a7.6 7.6 0 1 1-2.2-5.4" />
      <path d="M17.8 2.8v3.9h-3.9" />
    </>,
    p,
  );

/** A person, for the scan cards. */
export const BodyLine = (p: IconProps) =>
  line(
    <>
      <circle cx="12" cy="4.6" r="2.2" />
      <path d="M12 7v7M7 9.6h10M9.6 21l2.4-7 2.4 7" />
    </>,
    p,
  );

/** A tick in a circle. */
export const CheckCircle = (p: IconProps) =>
  line(
    <>
      <circle cx="12" cy="12" r="8.8" />
      <path d="m8.2 12.3 2.5 2.5 5.1-5.4" />
    </>,
    p,
  );

/** The outline the capture screen frames the patient in. Drawn on a 120 by 240 grid. */
export function BodyOutline({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 120 240" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <circle cx="60" cy="26" r="15" />
      <path d="M60 41v6M38 52c6-4 14-5 22-5s16 1 22 5l8 50M30 102l8-50M40 60l-2 66M80 60l2 66M42 126h36M44 126l-4 104M76 126l4 104M60 140v90" />
    </svg>
  );
}
